import React, { useState, useEffect } from 'react';
import { CheckAuth } from 'auth/auth';
import { useGetDashboardInitQuery } from 'redux_store/services/client/compositeInit';
import {
  useGetPersonalDetailsQuery,
} from 'redux_store/services/client/profile';
import { useGetB2cDocumentListQuery } from 'redux_store/services/client/document';
import { useDropdowns } from 'hooks/useDropdowns';
import Loader from 'wrapper/loader';

const PREFETCH_TIMEOUT_MS = 10000; // 10 seconds timeout

const B2CDataPreloader = ({ children, applicationId: propApplicationId }) => {
  const [timedOut, setTimedOut] = useState(false);

  // PRIORITY 2 declared first: the composite init also feeds the
  // applicationId fallback below.
  const { data: initData, isLoading: dashboardLoading, isUninitialized: dashboardUninitialized } =
    useGetDashboardInitQuery(undefined, { skip: false });

  // Get applicationId from props or cached auth data. Final fallback: the
  // init pipeline's first application — the cached-auth sources are all
  // primary-applicant concepts, so invited (junction) co-borrowers have
  // none of them and used to stall on the 10s timeout with the profile
  // and document prefetches skipped entirely.
  const cachedUser = CheckAuth() || {};
  const applicationId =
    propApplicationId ||
    cachedUser.applications?.[0]?.applicationId ||
    cachedUser.pending_application_id ||
    cachedUser.application_id ||
    localStorage.getItem('applicationId') ||
    sessionStorage.getItem('applicationId') ||
    initData?.data?.pipeline?.applications?.[0]?.application_details?.applicationId;
  const userId = cachedUser.user_id;

  // Determine if we should skip queries that require applicationId
  const skip = !applicationId;

  // The B2C documents endpoint REQUIRES borrowerId (it 400s on applicationId
  // alone — "borrowerId: Field required"). Derive it where we can; if we can't
  // yet, skip the prefetch entirely — the Documents page refetches with the
  // correct borrowerId on navigation. Prefetching with a missing borrowerId
  // only produced a guaranteed 400 and console noise.
  const borrowerId =
    cachedUser.borrower_id ||
    cachedUser.borrowerId ||
    cachedUser.applications?.[0]?.borrower_id ||
    initData?.data?.pipeline?.applications?.[0]?.application_details?.borrower_id ||
    null;
  const docsSkip = skip || !borrowerId;

  // PRIORITY 1: Borrower profile (critical for all downstream views)
  const { isLoading: profileLoading, isUninitialized: profileUninitialized } =
    useGetPersonalDetailsQuery({ applicationId }, { skip });

  // PRIORITY 3: Dropdown data (via unified hook)
  const { isLoading: dropdownLoading } =
    useDropdowns();

  // PRIORITY 4: Document list (needs borrowerId — see docsSkip above)
  const { isLoading: docsLoading, isUninitialized: docsUninitialized } =
    useGetB2cDocumentListQuery({ applicationId, borrowerId }, { skip: docsSkip });

  // Dashboard readiness: initData resolved and pipeline has applications (so selectedAction will be set)
  const hasPipelineApps = (initData?.data?.pipeline?.applications ?? []).length > 0;
  const isDashboardReady = !dashboardLoading && !dashboardUninitialized && (hasPipelineApps || !!initData);

  // Consider loading if any query is loading or dashboard not ready
  const isLoading =
    dashboardLoading ||
    dashboardUninitialized ||
    dropdownLoading ||
    (!skip && (profileLoading || profileUninitialized)) ||
    (!docsSkip && (docsLoading || docsUninitialized)) ||
    !isDashboardReady;

  // Timeout: proceed after 10 seconds even if still loading
  useEffect(() => {
    if (!isLoading) return;

    const timer = setTimeout(() => {
      setTimedOut(true);
    }, PREFETCH_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [isLoading]);

  // Show loading if still loading AND not timed out
  if (isLoading && !timedOut) {
    return <Loader variant="colorful" FullScreen={true} message="Preparing your dashboard..." subtitle="Loading your loan information" />;
  }

  return children;
};

export default B2CDataPreloader;
